import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ContentBuilderVersion {
  _id: string;
  content: string;
  instruction: string;
  createdAt: Date;
}

export interface ContentBuilderDraft {
  _id?: string;
  userId: string;
  contentId: string;
  title: string;
  content: string; // HTML content
  subject?: string;
  customization?: string; // "context"
  mediaContent?: { url: string; type: string; filename?: string }[];
  templateId?: string;
  model?: 'pro' | 'fast';
  type?: 'text' | 'mermaid';
  status: 'draft' | 'generating' | 'completed';
  versions?: ContentBuilderVersion[];
  currentVersionId?: string;
  createdAt: Date;
  updatedAt: Date;
}

@Injectable({
  providedIn: 'root',
})
export class ContentGeneratorService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl.replace(/\/$/, '')}/content-builder`;

  getDrafts(contentId: string, type?: 'text' | 'mermaid'): Observable<ContentBuilderDraft[]> {
    const url = type ? `${this.apiUrl}/drafts/${contentId}?type=${type}` : `${this.apiUrl}/drafts/${contentId}`;
    return this.http.get<ContentBuilderDraft[]>(url);
  }

  getDraft(draftId: string): Observable<ContentBuilderDraft> {
    return this.http.get<ContentBuilderDraft>(`${this.apiUrl}/draft/${draftId}`);
  }

  createDraft(params: { contentId: string; subject?: string; customization?: string; model?: 'pro' | 'fast'; templateId?: string }): Observable<ContentBuilderDraft> {
    return this.http.post<ContentBuilderDraft>(`${this.apiUrl}/draft`, params);
  }

  generateContent(params: {
    draftId?: string;
    contentId: string;
    subject: string;
    customization?: string;
    model: 'pro' | 'fast';
    count?: number;
    templateId?: string;
    mediaContexts?: { url: string; type: 'audio' | 'file' | 'video' | 'image'; filename?: string }[];
    attachedContentIds?: string[];
    attachedChunkIds?: string[];
    interactionId?: string;
  }): Observable<ContentBuilderDraft> {
    return this.http.post<ContentBuilderDraft>(`${this.apiUrl}/generate`, params);
  }

  addMediaContext(fileUri: string, mimeType: string, interactionId?: string | null): Observable<{ interactionId: string }> {
    return this.http.post<{ interactionId: string }>(`${this.apiUrl}/media-context`, { fileUri, mimeType, interactionId });
  }

  updateDraft(draftId: string, data: Partial<ContentBuilderDraft>): Observable<ContentBuilderDraft> {
    return this.http.put<ContentBuilderDraft>(`${this.apiUrl}/draft/${draftId}`, data);
  }

  deleteDraft(draftId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/draft/${draftId}`);
  }

  addVersion(
    draftId: string, 
    instruction: string, 
    baseContent: string, 
    model: 'pro' | 'fast' = 'fast', 
    templateId?: string, 
    mediaContexts?: { url: string; type: 'audio' | 'file' | 'video' | 'image'; filename?: string }[],
    attachedContentIds?: string[],
    attachedChunkIds?: string[]
  ): Observable<ContentBuilderDraft> {
    return this.http.post<ContentBuilderDraft>(`${this.apiUrl}/draft/${draftId}/versions`, {
      instruction,
      baseContent,
      model,
      templateId,
      mediaContexts,
      attachedContentIds,
      attachedChunkIds,
    });
  }

  restoreVersion(draftId: string, versionId: string): Observable<ContentBuilderDraft> {
    return this.http.put<ContentBuilderDraft>(`${this.apiUrl}/draft/${draftId}/versions/${versionId}/restore`, {});
  }

  deleteVersion(draftId: string, versionId: string): Observable<ContentBuilderDraft> {
    return this.http.delete<ContentBuilderDraft>(`${this.apiUrl}/draft/${draftId}/versions/${versionId}`);
  }
}
